import {MapPinIcon, PencilSquareIcon} from "@heroicons/react/20/solid";
import {OtsActionButton} from "../../components/OtsButton";
import {DescriptionItem} from "../../components/DescriptionList";
import {OtsDateTime} from "../../components/OtsDateTime";
import {MapInfoFields, MapInfoPanel, MapInfoSection} from "./MapInfoPanel";
import {formatCoordinates, type MapEntityRef, plainMapEntity, type RenderMapEntity} from "./map-panel";
import {OpsServiceStatusList, OpsStatusBadge, type OpsStatusView} from "./OpsServiceStatusList";

export type OpsStatusInfoPanelViewProps = {
  site: MapEntityRef;
  latitude?: number;
  longitude?: number;
  status: OpsStatusView;
  /** Service events the statuses were reported under. */
  events?: MapEntityRef[];
  onSetStatus?: () => void;
  renderEntity?: RenderMapEntity;
  onClose?: () => void;
};

/** HubGo's ops-mode panel for a site: its overall status, each service type's facets, and the events behind them. */
export const OpsStatusInfoPanelView = ({
  site,
  latitude,
  longitude,
  status,
  events = [],
  onSetStatus,
  renderEntity = plainMapEntity,
  onClose,
}: OpsStatusInfoPanelViewProps) => (
  <MapInfoPanel
    ariaLabel={`Operations status for ${site.name ?? "site"}`}
    icon={<MapPinIcon/>}
    title={renderEntity(site)}
    badges={<OpsStatusBadge status={status.status} label={status.label}/>}
    actions={onSetStatus && (
      <OtsActionButton variant="text" size="mini" className="!overflow-hidden" icon={<PencilSquareIcon/>} onClick={onSetStatus}>
        Set status
      </OtsActionButton>
    )}
    onClose={onClose}>
    <MapInfoSection title="Location">
      <MapInfoFields>
        <DescriptionItem label="GPS">{latitude != null && longitude != null ? formatCoordinates(latitude, longitude) : undefined}</DescriptionItem>
        <DescriptionItem label="Last status update">{status.asOf != null && <OtsDateTime value={status.asOf}/>}</DescriptionItem>
      </MapInfoFields>
    </MapInfoSection>
    {status.serviceTypes.length > 0 && (
      <MapInfoSection title="Service types">
        <OpsServiceStatusList serviceTypes={status.serviceTypes}/>
      </MapInfoSection>
    )}
    {events.length > 0 && (
      <MapInfoSection title="Related events">
        <ul className="!m-0 flex list-none flex-col gap-1 !p-0 text-xs">
          {events.map(event => (
            <li key={event.id} className="font-semibold text-[var(--ots-text)]">{renderEntity(event)}</li>
          ))}
        </ul>
      </MapInfoSection>
    )}
  </MapInfoPanel>
);
